/**
 * Technikal product-page behaviour for blocks/tk-*.liquid (ported from Solara Covers).
 *  - Live refresh: blocks marked [data-tk-live] are swapped from Horizon's re-rendered section on colour change.
 *  - Quantity breaks: tiles set Horizon's quantity box (automatic discounts do the money).
 *  - Trust row: dispatch countdown to the warehouse cutoff, refreshed every 30 s.
 *  - Phone check popup, promo end-date guard, copy-code chip.
 * Loaded as a module by each block; modules run once even when included several times.
 */

const track = (name, data) => {
  try { window.Shopify?.analytics?.publish?.(name, data); } catch { /* editor */ }
};

/* ---------- live refresh on colour change ---------- */
document.addEventListener(
  'shopify:product:select',
  (event) => {
    event.promise?.then(({ detail }) => {
      const html = detail?.html;
      if (!html?.querySelector) return;
      document.querySelectorAll('[data-tk-live]').forEach((el) => {
        const fresh = html.querySelector(`[data-tk-live="${el.dataset.tkLive}"]`);
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
      initQuantityBreaks();
      initPromo();
    }).catch(() => {});
  },
  true
);

/* ---------- quantity breaks ---------- */
function productScope(el) {
  return el.closest('[id*="ProductInformation-"], product-information, .product-information, main') || document;
}

function setQuantity(scope, qty) {
  const selector = scope.querySelector('quantity-selector-component');
  if (selector?.setValue) {
    selector.setValue(String(qty));
  }
  scope.querySelectorAll('input[name="quantity"]').forEach((input) => {
    input.value = String(qty);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

function initQuantityBreaks() {
  document.querySelectorAll('[data-tk-qb]').forEach((qb) => {
    const scope = productScope(qb);
    scope.classList.toggle('tk-qb-hide-stepper', qb.dataset.hideStepper === 'true');
    syncPick(qb);
    const checked = qb.querySelector('[data-tk-qty][aria-checked="true"]');
    if (checked) setQuantity(scope, checked.dataset.tkQty);
  });
}

/* ---------- free pick: "Buy 2, get 1 free" where the free unit is the shopper's choice ----------
   The picker sits under the tiles; the chosen option writes its quantity (3 cards, or 2 cards + accessory)
   and its extras (dock[, accessory variant]) onto the tier tile, which the normal tile + add-to-cart flow uses. */
function syncPick(qb) {
  const pick = qb?.querySelector('[data-tk-pick]');
  if (!pick) return;
  const tile = qb.querySelector(`[data-tk-qty][data-tier="${pick.dataset.tier}"]`);
  if (!tile) return;
  const opt = pick.querySelector('.tk-qb__pick-opt[aria-checked="true"]') || pick.querySelector('.tk-qb__pick-opt');
  if (!opt) return;
  tile.dataset.tkQty = opt.dataset.qty;
  tile.dataset.tkExtra = opt.dataset.extra || '';
  const was = tile.querySelector('[data-tk-was]');
  if (was && opt.dataset.was) was.textContent = opt.dataset.was;
  const save = tile.querySelector('[data-tk-save]');
  if (save && opt.dataset.save) save.textContent = opt.dataset.save;
  const accessory = Boolean(opt.dataset.product);
  tile.querySelectorAll('[data-tk-each]').forEach((el) => { el.hidden = accessory; }); // "per card" only fits the 3-card pick
  tile.querySelectorAll('[data-tk-times]').forEach((el) => { el.textContent = `×${opt.dataset.qty}`; });
  pick.querySelectorAll('[data-tk-pick-colour]').forEach((label) => { label.hidden = label.dataset.for !== opt.dataset.product; });
  const on = tile.getAttribute('aria-checked') === 'true';
  pick.hidden = !on;
  if (on) setQuantity(productScope(qb), opt.dataset.qty);
}

document.addEventListener('click', (event) => {
  const opt = event.target.closest?.('.tk-qb__pick-opt');
  if (!opt) return;
  const pick = opt.closest('[data-tk-pick]');
  pick.querySelectorAll('.tk-qb__pick-opt').forEach((o) => o.setAttribute('aria-checked', String(o === opt)));
  syncPick(opt.closest('[data-tk-qb]'));
  track('tk_free_pick', { product: opt.dataset.product || 'card' });
});

document.addEventListener('change', (event) => {
  const select = event.target.closest?.('[data-tk-pick-variant]');
  if (!select) return;
  const pick = select.closest('[data-tk-pick]');
  const opt = pick.querySelector(`.tk-qb__pick-opt[data-product="${select.dataset.tkPickVariant}"]`);
  if (!opt) return;
  const ids = (opt.dataset.extra || '').split(',').filter(Boolean);
  ids[ids.length - 1] = select.value; // last id = the accessory variant (the dock, if any, comes first)
  opt.dataset.extra = ids.join(',');
  const img = opt.querySelector('img');
  const chosen = select.selectedOptions[0];
  if (img && chosen?.dataset.img) { img.src = chosen.dataset.img; img.removeAttribute('srcset'); }
  syncPick(select.closest('[data-tk-qb]'));
});

document.addEventListener('click', (event) => {
  const tile = event.target.closest?.('[data-tk-qty]');
  if (!tile) return;
  const qb = tile.closest('[data-tk-qb]');
  qb.querySelectorAll('[data-tk-qty]').forEach((t) => t.setAttribute('aria-checked', String(t === tile)));
  syncPick(qb);
  setQuantity(productScope(qb), tile.dataset.tkQty);

  track('tk_pack_selected', { quantity: Number(tile.dataset.tkQty), tier: Number(tile.dataset.tier) });
});

// Shopper used − / + directly → highlight the matching tile (or none)
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'quantity') return;
  const qb = productScope(input).querySelector('[data-tk-qb]');
  if (!qb) return;
  // keep the shopper's tile when the box already shows its quantity (also our own setQuantity echo)
  const current = qb.querySelector('[data-tk-qty][aria-checked="true"]');
  if (current && current.dataset.tkQty === input.value) return;
  let matched = false;
  qb.querySelectorAll('[data-tk-qty]').forEach((t) => {
    // the combo tile (tier 4) adds a different product, so a plain quantity never selects it;
    // offer tiles with a free gift do match (the gift is armed from the selected tile at add-to-cart)
    const on = !matched && t.dataset.tier !== '4' && t.dataset.tkQty === input.value;
    if (on) matched = true;
    t.setAttribute('aria-checked', String(on));
  });
});

// Combo tile: arm the partner product right before Horizon's add-to-cart request (consumed in tk-commerce.js)
document.addEventListener(
  'click',
  (event) => {
    const submit = event.target.closest?.('product-form-component [type="submit"], .sticky-add-to-cart [type="submit"], [name="add"]');
    if (!submit) return;
    const qb = productScope(submit).querySelector('[data-tk-qb]') || document.querySelector('[data-tk-qb]');
    const tile = qb?.querySelector('[data-tk-qty][aria-checked="true"]');
    window.__tkCartExtras = (tile?.dataset.tkExtra || '').split(',').filter(Boolean).map((id) => ({ id, quantity: 1 }));
  },
  true
);

/* ---------- dispatch countdown ---------- */
function zonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', weekday: 'short',
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return { hour: Number(get('hour')), minute: Number(get('minute')), weekday: get('weekday') };
}

/** Next moment the warehouse cuts off, and the day it ships. */
function nextCutoff(timeZone, hour, shipsWeekends) {
  const now = new Date();
  // walk forward in 1-minute-precise steps by whole days; cheap and timezone-safe
  const local = zonedParts(now, timeZone);
  let minutesLeft = (hour - local.hour) * 60 - local.minute;
  let dayOffset = 0;
  let weekday = local.weekday;
  const isWeekend = (d) => d === 'Sat' || d === 'Sun';
  if (minutesLeft <= 0 || (!shipsWeekends && isWeekend(weekday))) {
    do {
      dayOffset += 1;
      weekday = zonedParts(new Date(now.getTime() + dayOffset * 864e5), timeZone).weekday;
    } while (!shipsWeekends && isWeekend(weekday));
    minutesLeft += dayOffset * 24 * 60;
  }
  const shipDate = new Date(now.getTime() + dayOffset * 864e5);
  return { minutesLeft: Math.max(minutesLeft, 1), shipDate };
}

function renderTrust() {
  document.querySelectorAll('[data-tk-tb]').forEach((tb) => {
    let tz = tb.dataset.tz || 'Asia/Shanghai';
    try { new Intl.DateTimeFormat('en', { timeZone: tz }); } catch { tz = 'Asia/Shanghai'; }
    const { minutesLeft, shipDate } = nextCutoff(tz, Number(tb.dataset.hour || 16), tb.dataset.weekends === 'true');
    const h = Math.floor(minutesLeft / 60);
    const m = minutesLeft % 60;
    const countdown = h > 0 ? `${h}h ${m}m` : `${m}m`;
    const locale = document.documentElement.lang || undefined;
    const today = new Date().toDateString() === shipDate.toDateString();
    const shipText = today ? 'today' : shipDate.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    const meta = tb.querySelector('[data-country-name]');
    tb.querySelectorAll('[data-tk-tb-text]').forEach((el) => {
      el.textContent = el.dataset.tkTbText
        .replaceAll('{countdown}', countdown)
        .replaceAll('{ship_date}', shipText)
        .replaceAll('{country_to}', meta?.dataset.countryTo || meta?.dataset.countryName || '')
        .replaceAll('{country_name}', meta?.dataset.countryName || '')
        .replaceAll('{country_flag}', meta?.dataset.countryFlag || '');
    });
  });
}

/* ---------- promo: end-date guard (cached pages) + copy code ---------- */
function initPromo() {
  document.querySelectorAll('[data-tk-end]').forEach((el) => {
    const end = el.dataset.tkEnd;
    if (end && new Date(`${end}T23:59:59`) < new Date()) el.hidden = true;
  });
}

document.addEventListener('click', async (event) => {
  const chip = event.target.closest?.('[data-tk-copy]');
  if (!chip) return;
  try { await navigator.clipboard.writeText(chip.dataset.tkCopy); } catch { return; }
  const before = chip.innerHTML;
  chip.textContent = chip.dataset.copied || 'Copied!';
  setTimeout(() => { chip.innerHTML = before; }, 1800);
});

/* ---------- phone check quiz (Solara fit-quiz behaviour: stacking steps) ---------- */
function quizReset(dlg) {
  dlg.querySelectorAll('[data-step]').forEach((s) => { s.hidden = s.dataset.step !== 's-phone'; });
  dlg.querySelectorAll('[data-result-step]').forEach((r) => { r.hidden = true; });
  dlg.querySelectorAll('.tk-q__tile.is-picked').forEach((b) => b.classList.remove('is-picked'));
  const sc = dlg.querySelector('[data-tk-q-scroll]'); if (sc) sc.scrollTop = 0;
}
function quizRenumber(dlg) {
  let n = 0;
  dlg.querySelectorAll('[data-step]').forEach((s) => { if (!s.hidden) { n += 1; const no = s.querySelector('[data-step-no]'); if (no) no.textContent = n; } });
}
document.addEventListener('click', (event) => {
  const opener = event.target.closest?.('[data-tk-compat-open]');
  if (opener) {
    const dlg = document.getElementById(`tk-compat-${opener.dataset.tkCompatOpen}`);
    if (!dlg) return;
    quizReset(dlg);
    dlg.showModal();
    track('tk_phone_check_open', {});
    return;
  }
  const dlg = event.target.closest?.('[data-tk-quiz]');
  if (!dlg) return;
  if (event.target === dlg || event.target.closest('[data-tk-compat-close]')) { dlg.close(); return; }
  if (event.target.closest('[data-tk-q-restart]')) { quizReset(dlg); return; }
  const tile = event.target.closest('.tk-q__tile');
  if (!tile) return;
  const step = tile.closest('[data-step]');
  step.querySelectorAll('.tk-q__tile').forEach((b) => b.classList.toggle('is-picked', b === tile));
  // changing an earlier answer hides everything after it (Solara behaviour)
  let after = false;
  dlg.querySelectorAll('[data-step], [data-result-step]').forEach((el) => {
    if (el === step) { after = true; return; }
    if (after) { el.hidden = true; el.querySelectorAll('.tk-q__tile.is-picked').forEach((b) => b.classList.remove('is-picked')); }
  });
  const next = tile.dataset.next;
  const target = dlg.querySelector(`[data-step="${next}"], [data-result-step="${next}"]`);
  if (target) {
    target.hidden = false;
    quizRenumber(dlg);
    requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
  try { sessionStorage.setItem('tk_phone', tile.dataset.answer); } catch { /* private mode */ }
  track('tk_phone_check_answer', { answer: tile.dataset.answer });
  if (target?.dataset.result) track('tk_phone_check_result', { result: target.dataset.result, step: next });
});

// "Add to cart" on a result → close the wizard and press the product's own Add to cart button
document.addEventListener('click', (event) => {
  const buy = event.target.closest?.('[data-tk-compat-buy]');
  if (!buy) return;
  buy.closest('dialog')?.close();
  const atc = productScope(buy).querySelector('product-form-component [type="submit"]') || document.querySelector('product-form-component [type="submit"]');
  if (atc && !atc.disabled) {
    atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
    atc.click();
  } else if (buy.dataset.buyUrl) {
    window.location.href = buy.dataset.buyUrl; // phone-check page / homepage: go to the product
  }
  track('tk_phone_check_add_to_cart', {});
});

// Deep link: any link to #phone-check opens the wizard (mega menu help card, ads, emails)
function openFromHash() {
  if (location.hash !== '#phone-check') return;
  const opener = document.querySelector('[data-tk-compat-open]');
  opener?.click();
}
window.addEventListener('hashchange', openFromHash);
if (document.readyState === 'complete') openFromHash();
else window.addEventListener('load', openFromHash, { once: true });

/* ---------- boot ---------- */
function boot() {
  initQuantityBreaks();
  initPromo();
  renderTrust();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
setInterval(renderTrust, 30000);
document.addEventListener('shopify:section:load', boot); // theme editor

/* ---------- video carousel + in-view autoplay (Solara video-slider port) ---------- */
const videoObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      for (const { target, isIntersecting } of entries) {
        if (isIntersecting) {
          if (target.dataset.src && !target.src) target.src = target.dataset.src; // load only when near
          target.play?.().catch(() => {});
        } else {
          target.pause?.();
        }
      }
    }, { rootMargin: '150px 0px', threshold: 0.35 })
  : null;

// Video cover pictures load only when the row is about a screen away (they were ~300 KB up front).
const posterObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      for (const { target, isIntersecting } of entries) {
        if (!isIntersecting) continue;
        if (target.dataset.poster && !target.poster) target.poster = target.dataset.poster;
        posterObserver.unobserve(target);
      }
    }, { rootMargin: '900px 0px' })
  : null;

function initVideos() {
  document.querySelectorAll('[data-tk-vs-video], video[data-tk-inview]').forEach((v) => {
    if (v.dataset.tkObserved) return;
    v.dataset.tkObserved = '1';
    if (v.dataset.poster) {
      if (posterObserver) posterObserver.observe(v);
      else v.poster = v.dataset.poster;
    }
    if (videoObserver) videoObserver.observe(v);
    else if (v.dataset.src) v.src = v.dataset.src;
  });
  document.querySelectorAll('[data-tk-vs-track]').forEach((track) => {
    if (track.dataset.tkInit) return;
    track.dataset.tkInit = '1';
    const markCenter = () => {
      const mid = track.scrollLeft + track.clientWidth / 2;
      let best = null; let dist = Infinity;
      for (const card of track.children) {
        const d = Math.abs(card.offsetLeft + card.offsetWidth / 2 - mid);
        if (d < dist) { dist = d; best = card; }
      }
      for (const card of track.children) card.classList.toggle('is-center', card === best);
    };
    track.addEventListener('scroll', () => requestAnimationFrame(markCenter), { passive: true });
    markCenter();
  });
}

document.addEventListener('click', (event) => {
  const nav = event.target.closest?.('[data-tk-vs-prev], [data-tk-vs-next]');
  if (nav) {
    const track = nav.parentElement.querySelector('[data-tk-vs-track]');
    const step = (track.firstElementChild?.offsetWidth || 270) + 20;
    track.scrollBy({ left: nav.hasAttribute('data-tk-vs-next') ? step : -step, behavior: 'smooth' });
    return;
  }
  const sound = event.target.closest?.('[data-tk-vs-sound]');
  if (sound) {
    const video = sound.parentElement.querySelector('video');
    const turnOn = video.muted;
    document.querySelectorAll('[data-tk-vs-video]').forEach((v) => { v.muted = true; });
    document.querySelectorAll('[data-tk-vs-sound]').forEach((b) => { b.textContent = '🔇'; });
    video.muted = !turnOn;
    sound.textContent = turnOn ? '🔊' : '🔇';
    if (turnOn) video.play?.().catch(() => {});
  }
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initVideos, { once: true });
else initVideos();
document.addEventListener('shopify:section:load', initVideos);

/* ---------- reward tooltip (Solara cashback) ---------- */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest?.('[data-tk-reward-toggle]');
  document.querySelectorAll('[data-tk-reward].is-open').forEach((r) => { if (!toggle || !r.contains(toggle)) r.classList.remove('is-open'); });
  if (!toggle) return;
  const box = toggle.closest('[data-tk-reward]');
  const open = box.classList.toggle('is-open');
  box.querySelector('.tk-reward__amount')?.setAttribute('aria-expanded', String(open));
  if (open) track('tk_reward_info', {});
});
// rolling deadline: the coming Friday (on a Friday: next week's), like Solara's "Order by Fri, Oct 16"
function fillFriday() {
  const d = new Date();
  d.setDate(d.getDate() + (((5 - d.getDay() + 7) % 7) || 7));
  const label = d.toLocaleDateString(document.documentElement.lang || undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  document.querySelectorAll('[data-tk-friday]').forEach((el) => { el.textContent = label; });
}
fillFriday();
// EUR amounts → visitor currency (Solara cashback did the same with Shopify.currency.rate)
function localiseEur() {
  const cur = window.Shopify?.currency;
  if (!cur || cur.active === 'EUR') return;
  document.querySelectorAll('[data-tk-eur]').forEach((el) => {
    const v = Math.round(Number(el.dataset.tkEur) * Number(cur.rate || 1));
    try { el.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cur.active, maximumFractionDigits: 0 }).format(v); } catch { /* keep EUR */ }
  });
}

/* ---------- count-up numbers (Solara photo-strip / reviews count) ---------- */
const countObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  for (const { target, isIntersecting } of entries) {
    if (!isIntersecting) continue;
    countObserver.unobserve(target);
    const end = Number(target.dataset.tkCountup || 0);
    const t0 = performance.now();
    const fmt = new Intl.NumberFormat(document.documentElement.lang || undefined);
    const step = (now) => {
      const p = Math.min(1, (now - t0) / 1400);
      target.textContent = fmt.format(Math.round(end * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}, { threshold: 0.6 }) : null;

/* ---------- scroll reveal (Solara: steps one by one + line fill, compare rows, cards) ---------- */
const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  for (const { target, isIntersecting } of entries) {
    if (!isIntersecting) continue;
    revealObserver.unobserve(target);
    target.classList.add('is-revealed');
  }
}, { threshold: 0.2, rootMargin: '0px 0px -8% 0px' }) : null;

function initMotion() {
  localiseEur();
  document.querySelectorAll('[data-tk-countup]').forEach((el) => {
    if (el.dataset.tkCountInit) return; el.dataset.tkCountInit = '1';
    if (countObserver && !matchMedia('(prefers-reduced-motion: reduce)').matches) { el.textContent = '0'; countObserver.observe(el); }
  });
  document.querySelectorAll('[data-tk-reveal]').forEach((el) => {
    if (el.dataset.tkRevealInit) return; el.dataset.tkRevealInit = '1';
    if (revealObserver && !matchMedia('(prefers-reduced-motion: reduce)').matches) revealObserver.observe(el);
    else el.classList.add('is-revealed');
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initMotion, { once: true });
else initMotion();
document.addEventListener('shopify:section:load', initMotion);

/* ---------- product tabs (Solara tabbed-featured-collections) ---------- */
document.addEventListener('click', (event) => {
  const tab = event.target.closest?.('[data-tk-tab]');
  if (!tab) return;
  const root = tab.closest('[data-tk-tabs]');
  root.querySelectorAll('[data-tk-tab]').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
  root.querySelectorAll('[data-tk-tab-panel]').forEach((p) => { p.hidden = p.dataset.tkTabPanel !== tab.dataset.tkTab; });
});
